import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  CheckCircle,
  Download,
  FileText,
  Mail,
  MessageCircle,
  PackagePlus,
  Plus,
  Printer,
  Trash2,
  UserPlus,
} from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import DatePicker from 'react-datepicker';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { ClientModal } from '@/components/clients/ClientModal';
import { ProductModal } from '@/components/products/ProductModal';
import { Button } from '@/components/ui/Button';
import { ConfirmDialog } from '@/components/ui/ConfirmDialog';
import { Input } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { Select } from '@/components/ui/Select';
import { useUppercaseInput } from '@/hooks/useUppercaseInput';
import { clientService } from '@/services/clients';
import { orderService } from '@/services/orders';
import { productService } from '@/services/products';
import { userService } from '@/services/users';
import { useAuthStore } from '@/stores/authStore';
import type { Client, Order, OrderStatus, Product, User } from '@/types/api';
import { resolveApiErrorMessage } from '@/utils/errors';
import { currencyFormatter, formatCurrencyInput, parseCurrencyInput } from '@/utils/formatters';
import { ORDER_STATUS_LABELS } from '@/constants/orderStatus';

const orderSchema = z.object({
  clientId: z.coerce
    .number({ invalid_type_error: 'Selecione um cliente' })
    .min(1, 'Selecione um cliente'),
  description: z
    .string()
    .max(255, 'A descrição deve ter no máximo 255 caracteres')
    .optional(),
  details: z.string().optional(),
  deliveryDate: z.date().optional(),
  freightValue: z
    .union([z.coerce.number().nonnegative('Informe um valor válido'), z.nan()])
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .optional(),
  discountValue: z
    .union([z.coerce.number().nonnegative('Informe um valor válido'), z.nan()])
    .transform((value) => (Number.isNaN(value) ? undefined : value))
    .optional(),
  status: z.enum(['PRODUCTION', 'PENDING', 'DELIVERED', 'PAID', 'FINISHED']).default('PRODUCTION'),
  items: z
    .array(
      z.object({
        productId: z.coerce.number().min(1, 'Selecione um produto'),
        qty: z.coerce.number().min(1, 'Quantidade mínima 1'),
        unitPrice: z.coerce.number().positive('Informe um valor'),
      }),
    )
    .min(1, 'Inclua pelo menos um item'),
});

type OrderFormValues = z.infer<typeof orderSchema>;

interface OrderCreateModalProps {
  isOpen: boolean;
  onClose: () => void;
  order?: Order;
  onSaved?: () => void;
}

const statusOptions: { value: OrderStatus; label: string }[] = [
  'PRODUCTION',
  'PENDING',
  'DELIVERED',
  'PAID',
  'FINISHED',
].map((status) => ({
  value: status as OrderStatus,
  label: ORDER_STATUS_LABELS[status as OrderStatus],
}));

const defaultValues: OrderFormValues = {
  clientId: 0,
  description: '',
  details: '',
  deliveryDate: undefined,
  freightValue: 0,
  discountValue: 0,
  status: 'PRODUCTION',
  items: [{ productId: 0, qty: 1, unitPrice: 0 }],
};

export const OrderCreateModal = ({ isOpen, onClose, order, onSaved }: OrderCreateModalProps) => {
  const isEditing = Boolean(order);
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.role === 'SUPERADMIN';
  const isAdmin = user?.role === 'ADMIN';
  const canViewAll = isSuperAdmin || isAdmin;
  const { handleUppercaseChange } = useUppercaseInput();
  const [selectedUserId, setSelectedUserId] = useState<number | undefined>(undefined);
  const [isClientModalOpen, setClientModalOpen] = useState(false);
  const [productModalState, setProductModalState] = useState<{
    open: boolean;
    index: number | null;
  }>({
    open: false,
    index: null,
  });
  const [availableProducts, setAvailableProducts] = useState<Product[]>([]);
  const [availableClients, setAvailableClients] = useState<Client[]>([]);
  const [isReviewOpen, setIsReviewOpen] = useState(false);
  const [reviewValues, setReviewValues] = useState<OrderFormValues | null>(null);
  const [isSummaryOpen, setIsSummaryOpen] = useState(false);
  const [summaryOrder, setSummaryOrder] = useState<Order | null>(null);
  const [itemToRemove, setItemToRemove] = useState<{ index: number; productName: string } | null>(
    null,
  );
  const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
  const initialPricingSnapshot = useRef<{
    items: Array<{ productId: number; qty: number; unitPrice: number }>;
    freightValue: number;
    discountValue: number;
  } | null>(null);

  const form = useForm<OrderFormValues>({
    resolver: zodResolver(orderSchema),
    defaultValues,
  });

  const { control, handleSubmit, reset, watch, setValue, formState } = form;
  
  // Monitorar o comprimento da descrição para mostrar alerta
  const descriptionValue = watch('description') || '';
  const descriptionLength = descriptionValue.length;
  const showDescriptionWarning = descriptionLength > 191;
  const itemsField = useFieldArray({ control, name: 'items' });
  const productMap = useMemo(() => {
    const map = new Map<number, Product>();
    availableProducts.forEach((product) => map.set(product.id, product));
    return map;
  }, [availableProducts]);

  useEffect(() => {
    if (!isOpen) return;

    if (order) {
      reset({
        clientId: order.client.id,
        description: order.description ?? '',
        details: order.details ?? '',
        deliveryDate: order.deliveryDate ? new Date(order.deliveryDate) : undefined,
        freightValue: order.freightValue ? Number(order.freightValue) : 0,
        discountValue: order.discountValue ? Number(order.discountValue) : 0,
        status: (order.status === 'CANCELLED' ? 'PENDING' : order.status) as 'PRODUCTION' | 'PENDING' | 'DELIVERED' | 'PAID' | 'FINISHED',
        items: order.items.map((item) => ({
          productId: item.product.id,
          qty: item.qty,
          unitPrice: Number(item.unitPrice),
        })),
      });
      initialPricingSnapshot.current = {
        items: order.items.map((item) => ({
          productId: item.product.id,
          qty: item.qty,
          unitPrice: Number(item.unitPrice),
        })),
        freightValue: order.freightValue ? Number(order.freightValue) : 0,
        discountValue: order.discountValue ? Number(order.discountValue) : 0,
      };
    } else {
      reset(defaultValues);
      initialPricingSnapshot.current = null;
    }
  }, [order, isOpen, reset]);

  const handleClose = () => {
    reset(defaultValues);
    setReviewValues(null);
    setIsReviewOpen(false);
    setSummaryOrder(null);
    setIsSummaryOpen(false);
    initialPricingSnapshot.current = null;
    onClose();
  };

  // Buscar usuários ADMIN_CLI para SUPERADMIN/ADMIN ao criar novo pedido
  const adminCliUsersQuery = useQuery({
    queryKey: ['users', 'admin-cli'],
    queryFn: async () => {
      try {
        const response = await userService.listAll();
        // Filtrar apenas usuários ADMIN_CLI
        const adminCliUsers = Array.isArray(response) 
          ? response.filter((u: any) => u && u.role === 'ADMIN_CLI')
          : [];
        return adminCliUsers;
      } catch (error) {
        console.error('Erro ao buscar usuários ADMIN_CLI:', error);
        return [];
      }
    },
    enabled: canViewAll && !isEditing, // Apenas ao criar novo pedido
  });

  const clientsQuery = useQuery({
    queryKey: ['clients', 'options', selectedUserId],
    queryFn: async () => {
      const response = await clientService.list({ 
        pageSize: 100,
        userId: selectedUserId || user?.id,
      });
      return response.data as Client[];
    },
    enabled: !canViewAll || !!selectedUserId, // Se for ADMIN/SUPERADMIN, precisa selecionar usuário primeiro
  });

  const productsQuery = useQuery({
    queryKey: ['products', 'options', selectedUserId],
    queryFn: async () => {
      const response = await productService.list({ 
        pageSize: 100,
        userId: selectedUserId || user?.id,
      });
      return response.data as Product[];
    },
    enabled: !canViewAll || !!selectedUserId, // Se for ADMIN/SUPERADMIN, precisa selecionar usuário primeiro
  });

  useEffect(() => {
    if (productsQuery.data) {
      setAvailableProducts((prev) => {
        const map = new Map<number, Product>();
        productsQuery.data.forEach((product) => map.set(product.id, product));
        prev.forEach((product) => {
          if (!map.has(product.id)) map.set(product.id, product);
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
      });
    }
  }, [productsQuery.data]);

  useEffect(() => {
    if (clientsQuery.data) {
      setAvailableClients((prev) => {
        const map = new Map<number, Client>();
        clientsQuery.data.forEach((client) => map.set(client.id, client));
        prev.forEach((client) => {
          if (!map.has(client.id)) map.set(client.id, client);
        });
        return Array.from(map.values()).sort((a, b) => a.name.localeCompare(b.name));
      });
    }
  }, [clientsQuery.data]);

  const handleClientSaved = async (savedClient: Client) => {
    // Invalidar todas as queries de clientes (inclui todas as variações com selectedUserId)
    await queryClient.invalidateQueries({ queryKey: ['clients', 'options'] });
    // Forçar refetch imediato da query atual de clientes
    await clientsQuery.refetch();
    
    // Atualizar estado local imediatamente (fallback caso o refetch demore)
    setAvailableClients((prev) => {
      const exists = prev.some((client) => client.id === savedClient.id);
      if (exists) {
        return prev.map((client) => (client.id === savedClient.id ? savedClient : client));
      }
      return [...prev, savedClient].sort((a, b) => a.name.localeCompare(b.name));
    });
    
    setClientModalOpen(false);
    setValue('clientId', savedClient.id, { shouldDirty: true, shouldValidate: true });
  };

  const closeProductModal = () => setProductModalState({ open: false, index: null });

  const handleProductSaved = async (savedProduct: Product) => {
    // Invalidar todas as queries de produtos (inclui todas as variações com selectedUserId)
    await queryClient.invalidateQueries({ queryKey: ['products', 'options'] });
    // Forçar refetch imediato da query atual de produtos
    await productsQuery.refetch();

    // Atualizar estado local imediatamente (fallback caso o refetch demore)
    setAvailableProducts((prev) => {
      const exists = prev.some((product) => product.id === savedProduct.id);
      if (exists) {
        return prev.map((product) => (product.id === savedProduct.id ? savedProduct : product));
      }
      return [...prev, savedProduct].sort((a, b) => a.name.localeCompare(b.name));
    });

    if (productModalState.index !== null) {
      const targetIndex = productModalState.index;
      setValue(`items.${targetIndex}.productId`, savedProduct.id, {
        shouldDirty: true,
        shouldValidate: true,
      });
      setValue(`items.${targetIndex}.unitPrice`, Number(savedProduct.unitPrice), {
        shouldDirty: true,
        shouldValidate: true,
      });
      void form.trigger('items');
    }

    closeProductModal();
  };

  const mutation = useMutation({
    mutationFn: async (values: OrderFormValues) => {
      const payload = {
        clientId: values.clientId,
        description: values.description?.trim().toUpperCase().substring(0, 191), // Truncar para 191 (limite do banco)
        details: values.details?.trim() || undefined,
        deliveryDate: values.deliveryDate ? values.deliveryDate.toISOString() : undefined,
        freightValue: values.freightValue,
        discountValue: values.discountValue,
        status: values.status,
        items: values.items.map((item) => ({
          productId: item.productId,
          qty: item.qty,
          unitPrice: item.unitPrice,
        })),
      };

      if (order) {
        return orderService.update(order.id, payload);
      }

      return orderService.create(payload);
    },
    onSuccess: (savedOrder: Order) => {
      toast.success(order ? 'Pedido atualizado' : 'Pedido criado com sucesso');
      onSaved?.();
      setIsReviewOpen(false);
      setReviewValues(null);
      handleClose();
      setSummaryOrder(savedOrder);
      setIsSummaryOpen(true);
    },
    onError: (error) => {
      toast.error(
        resolveApiErrorMessage(
          error,
          order ? 'Não foi possível atualizar o pedido' : 'Não foi possível criar o pedido',
        ),
      );
    },
  });

  const items = watch('items');
  const freightValue = watch('freightValue');
  const discountValue = watch('discountValue');

  // Estado para forçar recálculo do subtotal quando necessário
  const [subtotalRecalcTrigger, setSubtotalRecalcTrigger] = useState(0);

  const subtotal = useMemo(
    () => {
      const calculated = items.reduce((acc, item) => acc + item.qty * item.unitPrice, 0);
      return calculated;
    },
    [items, subtotalRecalcTrigger],
  );

  // Função para forçar recálculo do subtotal
  const forceSubtotalRecalc = useCallback(() => {
    setSubtotalRecalcTrigger((prev) => prev + 1);
  }, []);

  const total = useMemo(() => {
    const discount = discountValue ?? 0;
    const freight = freightValue ?? 0;
    return Math.max(subtotal + freight - discount, 0);
  }, [discountValue, freightValue, subtotal]);

  const summaryShareText = useMemo(() => {
    if (!summaryOrder) {
      return '';
    }
    const totalValue = currencyFormatter(Number(summaryOrder.totalValue ?? 0));
    return `Olá! Segue o resumo do pedido ${summaryOrder.number} no valor de ${totalValue}.`;
  }, [summaryOrder]);

  const summaryShareMessage = useMemo(
    () => (summaryShareText ? encodeURIComponent(summaryShareText) : ''),
    [summaryShareText],
  );

  const shouldOpenReview = useCallback(
    (values: OrderFormValues) => {
      if (!order) {
        return true;
      }

      const snapshot = initialPricingSnapshot.current;
      if (!snapshot) {
        return true;
      }

      const freight = values.freightValue ?? 0;
      const discount = values.discountValue ?? 0;
      if (Math.abs(snapshot.freightValue - freight) > 0.0001) {
        return true;
      }
      if (Math.abs(snapshot.discountValue - discount) > 0.0001) {
        return true;
      }

      if (snapshot.items.length !== values.items.length) {
        return true;
      }

      for (let index = 0; index < values.items.length; index += 1) {
        const current = values.items[index];
        const initial = snapshot.items[index];
        if (
          !initial ||
          initial.productId !== current.productId ||
          initial.qty !== current.qty ||
          Math.abs(initial.unitPrice - current.unitPrice) > 0.0001
        ) {
          return true;
        }
      }

      return false;
    },
    [order],
  );

  const onSubmit = (values: OrderFormValues) => {
    if (shouldOpenReview(values)) {
      setReviewValues(values);
      setIsReviewOpen(true);
      return;
    }
    setReviewValues(null);
    mutation.mutate(values);
  };

  const handleConfirmReview = () => {
    if (!reviewValues) {
      return;
    }
    mutation.mutate(reviewValues);
  };

  const handleCancelReview = () => {
    setIsReviewOpen(false);
    setReviewValues(null);
  };

  const closeSummaryModal = () => {
    if (isDownloadingDocument) {
      return;
    }
    setIsSummaryOpen(false);
    setSummaryOrder(null);
  };

  const handleDownloadReviewPdf = async (orderId: number) => {
    try {
      setIsDownloadingDocument(true);
      const blob = await orderService.downloadReviewPdf(orderId);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `revisao-pedido-${summaryOrder?.number ?? 'novo'}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success('PDF de revisão baixado com sucesso!');
    } catch (error) {
      console.error('Error downloading review PDF:', error);
      toast.error('Erro ao baixar PDF de revisão');
    } finally {
      setIsDownloadingDocument(false);
    }
  };

  const handleShareViaWhatsApp = async (orderId: number) => {
    try {
      const blob = await orderService.downloadReviewPdf(orderId);
      const url = window.URL.createObjectURL(blob);
      // Para WhatsApp, vamos criar um link de compartilhamento
      // Nota: WhatsApp Web não suporta anexos diretos, então vamos compartilhar o texto
      const order = summaryOrder;
      if (order) {
        const totalValue = currencyFormatter(Number(order.totalValue ?? 0));
        const message = encodeURIComponent(
          `Olá! Segue o resumo do pedido ${order.number} no valor de ${totalValue}.`,
        );
        window.open(`https://wa.me/?text=${message}`, '_blank');
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error sharing via WhatsApp:', error);
      toast.error('Erro ao compartilhar via WhatsApp');
    }
  };

  const handleShareViaEmail = async (orderId: number) => {
    try {
      const blob = await orderService.downloadReviewPdf(orderId);
      const url = window.URL.createObjectURL(blob);
      const order = summaryOrder;
      if (order) {
        const subject = encodeURIComponent(`Revisão do Pedido ${order.number}`);
        const body = encodeURIComponent(
          `Olá!\n\nSegue em anexo a revisão do pedido ${order.number}.\n\nCliente: ${order.client.name}\nTotal: ${currencyFormatter(Number(order.totalValue ?? 0))}`,
        );
        window.location.href = `mailto:${order.client.email ?? ''}?subject=${subject}&body=${body}`;
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error sharing via Email:', error);
      toast.error('Erro ao compartilhar via Email');
    }
  };

  const handlePrintReview = async (orderId: number) => {
    try {
      const blob = await orderService.downloadReviewPdf(orderId);
      const url = window.URL.createObjectURL(blob);
      const printWindow = window.open(url, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error printing review:', error);
      toast.error('Erro ao imprimir revisão');
    }
  };

  const handleDownloadContract = async () => {
    if (!summaryOrder) {
      return;
    }
    try {
      setIsDownloadingDocument(true);
      const blob = await orderService.downloadContract(summaryOrder.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `pedido-${summaryOrder.number.toLowerCase()}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error('Não foi possível gerar o PDF do pedido');
      console.error(error);
    } finally {
      setIsDownloadingDocument(false);
    }
  };

  const handleShareWhatsApp = async () => {
    if (!summaryOrder) {
      return;
    }

    try {
      setIsDownloadingDocument(true);
      const blob = await orderService.downloadContract(summaryOrder.id);
      const fileName = `pedido-${summaryOrder.number.toLowerCase()}.pdf`;
      const file = new File([blob], fileName, { type: 'application/pdf' });
      const nav =
        typeof navigator !== 'undefined'
          ? (navigator as Navigator & {
              canShare?: (data: ShareData) => boolean;
              share?: (data: ShareData) => Promise<void>;
            })
          : undefined;
      const canShareFile = nav?.canShare?.({ files: [file] }) ?? false;

      if (canShareFile && nav?.share) {
        await nav.share({
          files: [file],
          title: `Pedido ${summaryOrder.number}`,
          text: summaryShareText,
        });
      } else {
        const url = window.URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        link.remove();
        window.URL.revokeObjectURL(url);

        window.open(
          `https://wa.me/5511971227437?text=${summaryShareMessage}`,
          '_blank',
          'noopener,noreferrer',
        );
        toast.info('PDF baixado. Compartilhe o arquivo manualmente no WhatsApp.');
      }
    } catch (error) {
      toast.error('Não foi possível compartilhar o pedido via WhatsApp');
      console.error(error);
    } finally {
      setIsDownloadingDocument(false);
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        onClose={handleClose}
        title={isEditing ? 'Editar pedido' : 'Novo pedido'}
        description={
          isEditing
            ? 'Atualize os dados do pedido selecionado'
            : 'Preencha os dados para registrar um novo pedido'
        }
        wide
      >
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Seleção de usuário ADMIN_CLI (obrigatória para SUPERADMIN/ADMIN ao criar novo pedido) */}
          {canViewAll && !isEditing && (
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="selectedUserId">
                Usuário ADMIN_CLI <span className="text-danger">*</span>
              </label>
              <Select
                id="selectedUserId"
                value={selectedUserId || ''}
                onChange={(e) => {
                  const value = e.target.value ? Number(e.target.value) : undefined;
                  setSelectedUserId(value);
                  // Resetar cliente e produtos ao mudar usuário
                  setValue('clientId', 0);
                  form.resetField('items');
                }}
                required
              >
                <option value="">Selecione um usuário ADMIN_CLI</option>
                {adminCliUsersQuery.data?.map((adminUser: { id: number; name: string; email: string }) => (
                  <option key={adminUser.id} value={adminUser.id}>
                    {adminUser.name} ({adminUser.email})
                  </option>
                ))}
              </Select>
              {adminCliUsersQuery.isLoading && (
                <p className="mt-1 text-xs text-slate-500">Carregando usuários...</p>
              )}
              {!selectedUserId && adminCliUsersQuery.data && adminCliUsersQuery.data.length === 0 && (
                <p className="mt-1 text-xs text-danger">Nenhum usuário ADMIN_CLI encontrado.</p>
              )}
            </div>
          )}
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            {canViewAll && !isEditing && (
              <div className="sm:col-span-2">
                <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="adminCliUser">
                  Usuário ADMIN-CLI (Obrigatório)
                </label>
                <Select
                  id="adminCliUser"
                  value={selectedUserId || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setSelectedUserId(value ? Number(value) : undefined);
                    setValue('clientId', 0); // Reset cliente ao mudar usuário
                  }}
                  error={!selectedUserId && canViewAll ? 'Selecione um usuário' : undefined}
                >
                  <option value="">Selecione um usuário ADMIN-CLI</option>
                  {adminCliUsersQuery.data?.map((u) => (
                    <option key={u.id} value={u.id}>
                      {u.name} ({u.email})
                    </option>
                  ))}
                </Select>
              </div>
            )}
            <div>
              <div className="mb-1 grid grid-cols-[1fr_auto] items-center gap-2">
                <label className="text-sm font-medium text-slate-600" htmlFor="clientId">
                  Cliente
                </label>
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  className="gap-1 text-xs"
                  onClick={() => setClientModalOpen(true)}
                  disabled={canViewAll && !selectedUserId}
                >
                  <UserPlus className="size-4" /> Novo
                </Button>
              </div>
              <Controller
                control={control}
                name="clientId"
                render={({ field, fieldState }) => (
                  <Select
                    id="clientId"
                    value={field.value}
                    onChange={(event) => field.onChange(Number(event.target.value))}
                    error={fieldState.error?.message}
                    disabled={canViewAll && !isEditing && !selectedUserId}
                  >
                    <option value={0}>
                      {canViewAll && !isEditing && !selectedUserId
                        ? 'Selecione um usuário primeiro'
                        : 'Selecione um cliente'}
                    </option>
                    {availableClients.map((client) => (
                      <option key={client.id} value={client.id}>
                        {client.name}
                      </option>
                    ))}
                  </Select>
                )}
              />
              {canViewAll && !isEditing && !selectedUserId && (
                <p className="mt-1 text-xs text-slate-500">
                  Selecione um usuário ADMIN_CLI acima para carregar os clientes.
                </p>
              )}
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="status">
                Status
              </label>
              <Controller
                control={control}
                name="status"
                render={({ field }) => (
                  <Select
                    id="status"
                    value={field.value}
                    onChange={(event) => field.onChange(event.target.value)}
                  >
                    {statusOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Select>
                )}
              />
            </div>
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="deliveryDate">
              Data de entrega
            </label>
            <Controller
              control={control}
              name="deliveryDate"
              render={({ field }) => (
                <DatePicker
                  id="deliveryDate"
                  selected={field.value}
                  onChange={(date: Date | null) => field.onChange(date ?? undefined)}
                  className="h-10 w-full rounded-md border border-slate-200 px-3 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
                  dateFormat="dd/MM/yyyy"
                  placeholderText="Selecionar"
                  minDate={new Date()}
                />
              )}
            />
          </div>

          <div>
            <div className="mb-1 flex items-center justify-between">
              <label className="block text-sm font-medium text-slate-600" htmlFor="description">
                Descrição do pedido
              </label>
              {descriptionLength > 0 && (
                <span className={`text-xs ${showDescriptionWarning ? 'text-danger' : 'text-slate-500'}`}>
                  {descriptionLength}/191
                </span>
              )}
            </div>
            <textarea
              id="description"
              className={`h-24 w-full rounded-md border px-3 py-2 text-sm focus:outline-none focus:ring-2 ${
                showDescriptionWarning
                  ? 'border-danger focus:border-danger focus:ring-danger/20'
                  : 'border-slate-200 focus:border-primary-500 focus:ring-primary-200'
              }`}
              {...form.register('description', {
                onChange: handleUppercaseChange,
                maxLength: 255,
              })}
              placeholder="Descrição breve do pedido"
            />
            {showDescriptionWarning && (
              <div className="mt-1 rounded-md bg-danger/10 border border-danger/20 px-3 py-2">
                <p className="text-xs text-danger font-medium">
                  ⚠️ Limite do banco de dados: 191 caracteres
                </p>
                <p className="text-xs text-danger mt-1">
                  Você digitou {descriptionLength} caracteres. O texto será truncado para 191 caracteres ao salvar.
                </p>
              </div>
            )}
            {formState.errors.description && (
              <p className="mt-1 text-xs text-danger">{formState.errors.description.message}</p>
            )}
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="details">
              Detalhes
            </label>
            <textarea
              id="details"
              className="min-h-48 w-full rounded-md border border-slate-200 px-3 py-2 text-sm focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-200"
              {...form.register('details', {
                onChange: handleUppercaseChange,
              })}
              placeholder="Informações adicionais e detalhadas do pedido"
              rows={12}
            />
            {formState.errors.details && (
              <p className="mt-1 text-xs text-danger">{formState.errors.details.message}</p>
            )}
          </div>

          <div>
            <div className="flex items-center justify-between">
              <h4 className="text-sm font-semibold text-slate-700">Itens</h4>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => itemsField.append({ productId: 0, qty: 1, unitPrice: 0 })}
                className="gap-2"
              >
                <Plus className="size-4" /> Adicionar item
              </Button>
            </div>
            {formState.errors.items && (
              <p className="mt-1 text-xs text-danger">{formState.errors.items.message}</p>
            )}

            <div className="mt-3 space-y-3">
              {itemsField.fields.map((fieldItem, index) => (
                <div
                  key={fieldItem.id}
                  className="grid grid-cols-12 gap-3 rounded-lg border border-slate-100 bg-slate-50/60 p-4"
                >
                  <div className="col-span-12 sm:col-span-5">
                    <div className="mb-1 grid grid-cols-[1fr_auto] items-center gap-2">
                      <label
                        className="text-xs font-medium text-slate-500"
                        htmlFor={`product-${fieldItem.id}`}
                      >
                        Produto
                      </label>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="gap-1 text-xs"
                        onClick={() => setProductModalState({ open: true, index })}
                      >
                        <PackagePlus className="size-4" /> Novo
                      </Button>
                    </div>
                    <Controller
                      control={control}
                      name={`items.${index}.productId`}
                      render={({ field, fieldState }) => (
                        <Select
                          id={`product-${fieldItem.id}`}
                          value={field.value}
                          onChange={(event) => {
                            const productId = Number(event.target.value);
                            field.onChange(productId);
                            const product = availableProducts.find((p) => p.id === productId);
                            if (product) {
                              setValue(`items.${index}.unitPrice`, Number(product.unitPrice), {
                                shouldDirty: true,
                                shouldValidate: true,
                              });
                              void form.trigger('items');
                            }
                          }}
                          error={fieldState.error?.message}
                          disabled={canViewAll && !isEditing && !selectedUserId}
                        >
                          <option value={0}>
                            {canViewAll && !isEditing && !selectedUserId
                              ? 'Selecione um usuário primeiro'
                              : 'Selecione'}
                          </option>
                          {availableProducts.map((product) => (
                            <option key={product.id} value={product.id}>
                              {product.name}
                            </option>
                          ))}
                        </Select>
                      )}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-2">
                    <label
                      className="mb-1 block text-xs font-medium text-slate-500"
                      htmlFor={`qty-${fieldItem.id}`}
                    >
                      Qtd
                    </label>
                    <Controller
                      control={control}
                      name={`items.${index}.qty`}
                      render={({ field, fieldState }) => (
                        <Input
                          id={`qty-${fieldItem.id}`}
                          type="number"
                          min={1}
                          value={field.value}
                          onChange={(event) => field.onChange(Number(event.target.value))}
                          onBlur={() => {
                            field.onBlur();
                            // Forçar recálculo do subtotal ao sair do campo
                            forceSubtotalRecalc();
                          }}
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-3">
                    <label
                      className="mb-1 block text-xs font-medium text-slate-500"
                      htmlFor={`unit-price-${fieldItem.id}`}
                    >
                      Valor unitário
                    </label>
                    <Controller
                      control={control}
                      name={`items.${index}.unitPrice`}
                      render={({ field, fieldState }) => (
                        <Input
                          id={`unit-price-${fieldItem.id}`}
                          value={formatCurrencyInput(field.value)}
                          onChange={(event) =>
                            field.onChange(parseCurrencyInput(event.target.value))
                          }
                          onBlur={() => {
                            field.onBlur();
                            // Forçar recálculo do subtotal ao sair do campo
                            forceSubtotalRecalc();
                          }}
                          inputMode="decimal"
                          error={fieldState.error?.message}
                        />
                      )}
                    />
                  </div>
                  <div className="col-span-6 sm:col-span-1">
                    <label
                      className="mb-1 block text-xs font-medium text-slate-500"
                      htmlFor={`subtotal-${fieldItem.id}`}
                    >
                      Subtotal
                    </label>
                    <Input
                      id={`subtotal-${fieldItem.id}`}
                      value={currencyFormatter(
                        (items[index]?.qty ?? 0) * (items[index]?.unitPrice ?? 0) || 0,
                      )}
                      disabled
                    />
                  </div>
                  <div className="col-span-6 flex items-end justify-end gap-1 sm:col-span-1">
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        // Confirmar produto e recalcular subtotal
                        forceSubtotalRecalc();
                        toast.success('Produto confirmado');
                      }}
                      aria-label="Confirmar produto"
                      title="Confirmar produto no pedido"
                      className="text-green-600 hover:bg-green-50 hover:text-green-700"
                    >
                      <CheckCircle className="size-4" />
                    </Button>
                    <Button
                      type="button"
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const productId = fieldItem.productId;
                        const product = availableProducts.find((p) => p.id === productId);
                        const productName = product?.name || 'este item';
                        if (window.confirm(`Tem certeza que deseja remover "${productName}" do pedido?`)) {
                          itemsField.remove(index);
                        }
                      }}
                      aria-label="Remover item"
                      title="Remover produto do pedido"
                    >
                      <Trash2 className="size-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="freightValue">
                Frete
              </label>
              <Controller
                control={control}
                name="freightValue"
                render={({ field, fieldState }) => (
                  <Input
                    id="freightValue"
                    value={formatCurrencyInput(field.value)}
                    onChange={(event) => field.onChange(parseCurrencyInput(event.target.value))}
                    inputMode="decimal"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
            <div>
              <label className="mb-1 block text-sm font-medium text-slate-600" htmlFor="discountValue">
                Desconto
              </label>
              <Controller
                control={control}
                name="discountValue"
                render={({ field, fieldState }) => (
                  <Input
                    id="discountValue"
                    value={formatCurrencyInput(field.value)}
                    onChange={(event) => field.onChange(parseCurrencyInput(event.target.value))}
                    inputMode="decimal"
                    error={fieldState.error?.message}
                  />
                )}
              />
            </div>
          </div>

          <div className="flex flex-col items-end gap-1 text-sm">
            <div className="flex w-full max-w-xs justify-between text-slate-600">
              <span>Subtotal</span>
              <span>{currencyFormatter(subtotal)}</span>
            </div>
            <div className="flex w-full max-w-xs justify-between text-slate-600">
              <span>Frete</span>
              <span>{currencyFormatter(freightValue ?? 0)}</span>
            </div>
            <div className="flex w-full max-w-xs justify-between text-slate-600">
              <span>Desconto</span>
              <span>- {currencyFormatter(discountValue ?? 0)}</span>
            </div>
            <div className="flex w-full max-w-xs justify-between text-base font-semibold text-slate-700">
              <span>Total</span>
              <span>{currencyFormatter(total)}</span>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <Button type="button" variant="ghost" onClick={handleClose}>
              Cancelar
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending
                ? 'Salvando...'
                : isEditing
                  ? 'Salvar alterações'
                  : 'Criar pedido'}
            </Button>
          </div>
        </form>
      </Modal>

      <ClientModal
        isOpen={isClientModalOpen}
        onClose={() => setClientModalOpen(false)}
        onSaved={handleClientSaved}
      />
      <ProductModal
        isOpen={productModalState.open}
        onClose={closeProductModal}
        onSaved={handleProductSaved}
      />

      <Modal
        isOpen={isReviewOpen && Boolean(reviewValues)}
        onClose={handleCancelReview}
        title="Revisar pedido"
        description="Verifique os dados antes de confirmar."
        wide
      >
        {reviewValues && (
          <div className="space-y-4">
            <div>
              <h4 className="text-sm font-semibold text-slate-700">Informações gerais</h4>
              <div className="mt-2 grid gap-2 text-sm text-slate-600 sm:grid-cols-2">
                <div>
                  <span className="block font-medium text-slate-700">Cliente</span>
                  <span>
                    {clientsQuery.data?.find((client) => client.id === reviewValues.clientId)
                      ?.name ?? 'Cliente não encontrado'}
                  </span>
                </div>
                <div>
                  <span className="block font-medium text-slate-700">Status</span>
                  <span>{ORDER_STATUS_LABELS[reviewValues.status]}</span>
                </div>
                <div>
                  <span className="block font-medium text-slate-700">Entrega prevista</span>
                  <span>
                    {reviewValues.deliveryDate
                      ? new Intl.DateTimeFormat('pt-BR').format(reviewValues.deliveryDate)
                      : 'Não definido'}
                  </span>
                </div>
                <div>
                  <span className="block font-medium text-slate-700">Frete</span>
                  <span>{currencyFormatter(reviewValues.freightValue ?? 0)}</span>
                </div>
              <div>
                <span className="block font-medium text-slate-700">Desconto</span>
                <span>{currencyFormatter(reviewValues.discountValue ?? 0)}</span>
              </div>
              </div>
              {reviewValues.description && (
                <div className="mt-3">
                  <p className="mb-1 text-xs font-semibold uppercase text-slate-500">Descrição do pedido:</p>
                  <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600">
                    {reviewValues.description}
                  </p>
                </div>
              )}
              {reviewValues.details && (
                <div className="mt-3">
                  <p className="mb-1 text-xs font-semibold uppercase text-slate-500">Detalhes:</p>
                  <p className="rounded-md bg-slate-50 p-3 text-sm text-slate-600 whitespace-pre-wrap">
                    {reviewValues.details}
                  </p>
                </div>
              )}
            </div>

            <div>
              <h4 className="text-sm font-semibold text-slate-700">Itens</h4>
              <div className="mt-2 overflow-hidden rounded-lg border border-slate-200">
                <table className="min-w-full text-sm text-slate-600">
                  <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                    <tr>
                      <th className="px-4 py-2 text-left">Produto</th>
                      <th className="px-4 py-2 text-right">Qtd</th>
                      <th className="px-4 py-2 text-right">Valor unitário</th>
                      <th className="px-4 py-2 text-right">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {reviewValues.items.map((item, index) => {
                      const product = productMap.get(item.productId);
                      const subtotalValue = item.qty * item.unitPrice;
                      return (
                        <tr
                          key={`${item.productId}-${index}`}
                          className="border-t border-slate-100"
                        >
                          <td className="px-4 py-2">
                            {product?.name ?? `Produto ${item.productId}`}
                          </td>
                          <td className="px-4 py-2 text-right">{item.qty}</td>
                          <td className="px-4 py-2 text-right">
                            {currencyFormatter(item.unitPrice)}
                          </td>
                          <td className="px-4 py-2 text-right">
                            {currencyFormatter(subtotalValue)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            <div className="flex justify-end text-sm text-slate-600">
              <div className="w-full max-w-xs space-y-1">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>
                    {currencyFormatter(
                      reviewValues.items.reduce((acc, item) => acc + item.qty * item.unitPrice, 0),
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Frete</span>
                  <span>{currencyFormatter(reviewValues.freightValue ?? 0)}</span>
                </div>
                <div className="flex justify-between">
                  <span>Desconto</span>
                  <span>- {currencyFormatter(reviewValues.discountValue ?? 0)}</span>
                </div>
                <div className="flex justify-between font-semibold text-slate-800">
                  <span>Total</span>
                  <span>
                    {currencyFormatter(
                      Math.max(
                        reviewValues.items.reduce((acc, item) => acc + item.qty * item.unitPrice, 0) +
                          (reviewValues.freightValue ?? 0) -
                          (reviewValues.discountValue ?? 0),
                        0,
                      ),
                    )}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2">
              <Button
                variant="secondary"
                onClick={handleCancelReview}
                disabled={mutation.isPending}
              >
                Voltar
              </Button>
              <Button onClick={handleConfirmReview} disabled={mutation.isPending}>
                {mutation.isPending ? 'Confirmando...' : 'Confirmar pedido'}
              </Button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        isOpen={isSummaryOpen && Boolean(summaryOrder)}
        onClose={closeSummaryModal}
        title="Resumo do pedido"
        description="Pedido registrado com sucesso. Compartilhe ou gere um PDF opcional."
        wide
      >
        {summaryOrder && (
          <div className="space-y-4">
            <div className="rounded-lg border border-slate-200 bg-slate-50 p-4 text-sm text-slate-600">
              <p className="font-semibold text-slate-800">Pedido {summaryOrder.number}</p>
              <p>Cliente: {summaryOrder.client.name}</p>
              <p>Email: {summaryOrder.client.email ?? '-'}</p>
              <p>Telefone: {summaryOrder.client.phone ?? '-'}</p>
            </div>

            <div className="overflow-hidden rounded-lg border border-slate-200">
              <table className="min-w-full text-sm text-slate-600">
                <thead className="bg-slate-50 text-xs uppercase text-slate-500">
                  <tr>
                    <th className="px-4 py-2 text-left">Produto</th>
                    <th className="px-4 py-2 text-right">Qtd</th>
                    <th className="px-4 py-2 text-right">Valor unitário</th>
                    <th className="px-4 py-2 text-right">Subtotal</th>
                  </tr>
                </thead>
                <tbody>
                  {summaryOrder.items.map((item) => (
                    <tr key={item.id} className="border-t border-slate-100">
                      <td className="px-4 py-2">{item.product.name}</td>
                      <td className="px-4 py-2 text-right">{item.qty}</td>
                      <td className="px-4 py-2 text-right">
                        {currencyFormatter(Number(item.unitPrice))}
                      </td>
                      <td className="px-4 py-2 text-right">
                        {currencyFormatter(Number(item.subtotal))}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end text-sm text-slate-600">
              <div className="w-full max-w-xs space-y-1">
                <div className="flex justify-between">
                  <span>Subtotal</span>
                  <span>
                    {currencyFormatter(
                      summaryOrder.items.reduce((acc, item) => acc + Number(item.subtotal), 0),
                    )}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span>Frete</span>
                  <span>{currencyFormatter(Number(summaryOrder.freightValue ?? 0))}</span>
                </div>
                <div className="flex justify-between">
                  <span>Desconto</span>
                  <span>- {currencyFormatter(Number(summaryOrder.discountValue ?? 0))}</span>
                </div>
                <div className="flex justify-between font-semibold text-slate-800">
                  <span>Total</span>
                  <span>{currencyFormatter(Number(summaryOrder.totalValue ?? 0))}</span>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap justify-end gap-2">
              <Button variant="ghost" onClick={closeSummaryModal} disabled={isDownloadingDocument}>
                Fechar
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (summaryOrder) {
                    handleShareViaWhatsApp(summaryOrder.id);
                  }
                }}
                disabled={isDownloadingDocument}
                title="Compartilhar via WhatsApp"
              >
                <MessageCircle className="mr-2 size-4" /> WhatsApp
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (summaryOrder) {
                    handleShareViaEmail(summaryOrder.id);
                  }
                }}
                title="Enviar por e-mail"
                disabled={isDownloadingDocument}
              >
                <Mail className="mr-2 size-4" /> Email
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (summaryOrder) {
                    handlePrintReview(summaryOrder.id);
                  }
                }}
                disabled={isDownloadingDocument}
                title="Imprimir revisão"
              >
                <Printer className="mr-2 size-4" /> Imprimir
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  if (summaryOrder) {
                    handleDownloadReviewPdf(summaryOrder.id);
                  }
                }}
                disabled={isDownloadingDocument}
                title="Baixar PDF de revisão"
                className="gap-2"
              >
                {isDownloadingDocument ? (
                  'Gerando...'
                ) : (
                  <>
                    <Download className="size-4" /> Baixar PDF Revisão
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleDownloadContract}
                disabled={isDownloadingDocument}
                title="Gerar contrato (opcional)"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                <FileText className="mr-2 size-4" /> Gerar Contrato
              </Button>
            </div>

            <p className="text-xs text-slate-500">
              Compartilhe o resumo com o cliente e, se preferir, utilize o PDF como comprovante.
            </p>
          </div>
        )}
      </Modal>

      <ConfirmDialog
        isOpen={Boolean(itemToRemove)}
        onClose={() => setItemToRemove(null)}
        onConfirm={() => {
          if (itemToRemove) {
            itemsField.remove(itemToRemove.index);
            setItemToRemove(null);
          }
        }}
        title="Confirmar remoção"
        message={
          itemToRemove
            ? `Tem certeza que deseja remover o item "${itemToRemove.productName}" do pedido?`
            : ''
        }
        confirmText="Remover"
        cancelText="Cancelar"
        variant="warning"
      />
    </>
  );
};
